/**
 * Video Call View Component
 * Shared presentation layer for consultation call states and controls
 */

import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { User, Maximize2, Minimize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { VideoCallControls } from "./VideoCallControls";
import { NetworkQualityIndicator } from "./NetworkQualityIndicator";
import { CallingView } from "./CallingView";
import { CallEndedView } from "./CallEndedView";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { NetworkQuality } from "@/types/video-call";
import {
  videoCallMutedOverlayClassName,
  videoCallStatusPillClassName,
  videoCallSurfaceClassName,
} from "./video-call-theme";

interface VideoCallViewProps {
  // State
  isConnecting: boolean;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  isMuted: boolean;
  isCallEnded: boolean;
  localUserJoined: boolean;
  remoteUserJoined: boolean;
  networkQuality: NetworkQuality;
  callDuration: string;
  error: string | null;
  initError: string | null;
  isInitializing: boolean;
  isRejoining: boolean;

  // Refs
  localVideoRef: React.RefCallback<HTMLDivElement>;
  remoteVideoRef: React.RefObject<HTMLDivElement>;

  // Actions
  onToggleMute: () => void;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onEndCall: () => void;
  onRejoin: () => void;

  // UI props
  patientName?: string;
  isMaximized?: boolean;
  compact?: boolean;
  onMaximize?: () => void;
  onMinimize?: () => void;
  onNotifyPatient?: () => void;
  className?: string;
}

export const VideoCallView: React.FC<VideoCallViewProps> = ({
  isConnecting,
  isVideoEnabled,
  isAudioEnabled,
  isMuted,
  isCallEnded,
  localUserJoined,
  remoteUserJoined,
  networkQuality,
  callDuration,
  error,
  initError,
  isInitializing,
  isRejoining,
  localVideoRef,
  remoteVideoRef,
  onToggleMute,
  onToggleVideo,
  onToggleAudio,
  onEndCall,
  onRejoin,
  patientName = "Patient",
  isMaximized = false,
  compact = false,
  onMaximize,
  onMinimize,
  onNotifyPatient,
  className,
}) => {
  const containerSize = isMaximized
    ? "h-full w-full"
    : "min-h-80 w-full md:min-h-96";

  const localVideoSize = isMaximized
    ? "w-44 h-[128px]"
    : compact
      ? "w-[100px] h-[75px]"
      : "w-32 h-24";

  const [showEndCallConfirm, setShowEndCallConfirm] = useState(false);

  // --- Draggable PiP state ---
  const pipRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{
    dragging: boolean;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const [pipOffset, setPipOffset] = useState<{ x: number; y: number } | null>(
    null
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!pipRef.current) return;
      e.preventDefault();
      pipRef.current.setPointerCapture(e.pointerId);
      const rect = pipRef.current.getBoundingClientRect();
      dragState.current = {
        dragging: true,
        startX: e.clientX,
        startY: e.clientY,
        origX: pipOffset?.x ?? 0,
        origY: pipOffset?.y ?? 0,
      };
      // Suppress transition during drag
      pipRef.current.style.transition = "none";
    },
    [pipOffset]
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current?.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPipOffset({
      x: dragState.current.origX + dx,
      y: dragState.current.origY + dy,
    });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragState.current) return;
    dragState.current = null;
    if (pipRef.current) {
      pipRef.current.style.transition = "";
    }
  }, []);

  // Reset PiP position when toggling maximized mode
  const pipStyle: React.CSSProperties = pipOffset
    ? { transform: `translate(${pipOffset.x}px, ${pipOffset.y}px)` }
    : {};

  // Render call ended view with rejoin option
  if (isCallEnded && !isRejoining) {
    return (
      <div
        className={cn(
          "relative rounded-xl overflow-hidden",
          containerSize,
          className
        )}
      >
        <CallEndedView
          callDuration={callDuration}
          onRejoin={onRejoin}
          isRejoining={isRejoining}
        />
      </div>
    );
  }

  const content = (
    <div
      className={cn(
        "relative overflow-hidden",
        isMaximized ? "bg-muted" : videoCallSurfaceClassName,
        !isMaximized && "rounded-lg",
        isMaximized ? "h-full w-full fixed inset-0 z-[100]" : containerSize,
        !isMaximized && className
      )}
    >
      {/* Background — remote video when joined, CallingView when waiting */}
      <div className="absolute inset-0">
        {remoteUserJoined ? (
          <div
            ref={remoteVideoRef}
            className="w-full h-full [&_video]:w-full [&_video]:h-full [&_video]:object-cover"
          />
        ) : (
          <CallingView
            patientName={patientName}
            waitingTime={callDuration}
            compact={compact}
            onNotifyPatient={onNotifyPatient}
          />
        )}
      </div>

      {/* Local Video PiP — always a small draggable overlay */}
      <div
        ref={pipRef}
        className={cn(
          "absolute z-5 overflow-hidden cursor-grab active:cursor-grabbing touch-none select-none",
          "border-2 border-border bg-card shadow-md",
          "[&_video]:w-full [&_video]:h-full [&_video]:object-cover",
          isMaximized
            ? "bottom-20 left-4 rounded-lg"
            : compact
              ? "bottom-14 left-3 rounded-lg"
              : "bottom-24 left-4 rounded-xl",
          localVideoSize
        )}
        style={pipStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <div
          ref={localVideoRef}
          className="w-full h-full"
          style={{
            display: localUserJoined && isVideoEnabled ? "block" : "none",
          }}
        />
        {(!localUserJoined || !isVideoEnabled) && (
          <div
            className={cn(
              "absolute inset-0 flex h-full w-full items-center justify-center",
              videoCallMutedOverlayClassName
            )}
          >
            <User
              className={cn(
                "text-muted-foreground",
                isMaximized ? "h-6 w-6" : compact ? "h-5 w-5" : "h-8 w-8"
              )}
            />
          </div>
        )}
      </div>

      {/* Call Duration */}
      {(localUserJoined || remoteUserJoined) && (
        <div
          className={cn(
            "absolute z-10 rounded-full",
            videoCallStatusPillClassName,
            compact ? "top-2 left-2 px-2 py-1" : "top-4 left-4 px-3 py-1.5"
          )}
        >
          <span
            className={cn(
              "font-medium text-foreground",
              compact ? "text-xs" : "text-sm"
            )}
          >
            {callDuration}
          </span>
        </div>
      )}

      {/* Network Quality */}
      {localUserJoined && (
        <div
          className={cn(
            "absolute z-10",
            compact ? "top-2 left-20" : "top-4 left-20"
          )}
        >
          <NetworkQualityIndicator quality={networkQuality} />
        </div>
      )}

      {/* Maximize/Minimize Button */}
      {(onMinimize || onMaximize) && (
        <button
          onClick={isMaximized ? onMinimize : onMaximize}
          aria-label={
            isMaximized
              ? "Minimize consultation view"
              : "Maximize consultation view"
          }
          className={cn(
            "absolute z-10 flex items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90",
            compact ? "top-2 right-2 h-8 w-8" : "top-4 right-4 h-10 w-10"
          )}
          title={isMaximized ? "Minimize" : "Maximize"}
        >
          {isMaximized ? (
            <Minimize2 className={compact ? "h-4 w-4" : "h-5 w-5"} />
          ) : (
            <Maximize2 className={compact ? "h-4 w-4" : "h-5 w-5"} />
          )}
        </button>
      )}

      {/* Controls */}
      <div
        className={cn(
          "absolute left-1/2 -translate-x-1/2 z-10",
          compact ? "bottom-2" : "bottom-4"
        )}
      >
        <VideoCallControls
          isMuted={isMuted}
          isVideoEnabled={isVideoEnabled}
          isAudioEnabled={isAudioEnabled}
          onToggleMute={onToggleMute}
          onToggleVideo={onToggleVideo}
          onToggleAudio={onToggleAudio}
          onEndCall={() => setShowEndCallConfirm(true)}
          compact={compact}
        />
      </div>

      {/* Error Message */}
      {(error || initError) && (
        <div className="absolute inset-x-4 top-16 z-20 rounded-lg border border-destructive/40 bg-destructive/15 px-4 py-2 text-center text-sm text-destructive shadow-sm backdrop-blur-sm md:left-1/2 md:right-auto md:w-full md:max-w-md md:-translate-x-1/2">
          {initError || error}
        </div>
      )}

      {/* Loading Overlay */}
      {(isInitializing || isConnecting) && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-card/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border/60 bg-card p-6 shadow-sm">
            <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-foreground">
              {isInitializing ? "Initializing call..." : "Connecting..."}
            </p>
          </div>
        </div>
      )}

      {/* End Call Confirmation */}
      <AlertDialog
        open={showEndCallConfirm}
        onOpenChange={setShowEndCallConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End consultation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect the call and end the current session. Are you
              sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onEndCall}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              End Call
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // Portal the maximized view to body so it sits above all stacking contexts
  if (isMaximized) {
    return createPortal(content, document.body);
  }

  return content;
};

export default VideoCallView;
